import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Upload } from "lucide-react";
import { Area, Card, DeleteBtn, Field, Input, ObjectList, PageTitle, Skel, StringList, YBtn, uploadFile, useSaver } from "@/admin/ui";
import { adminApi, useAdmin, useRefresh } from "@/admin/lib";
import { BACKEND_URL, errorMessage, mediaUrl } from "@/lib/api";

const useDoc = (path) => {
  const { data } = useAdmin(path);
  const [d, setD] = useState(null);
  useEffect(() => { if (data) setD(data); }, [data]);
  return [d, setD];
};

const T = ({ d, setD, path, label, area, testId }) => {
  const get = path.split(".").reduce((o, k) => o?.[k], d);
  const set = (v) => {
    const keys = path.split(".");
    const next = structuredClone(d);
    keys.slice(0, -1).reduce((o, k) => (o[k] = o[k] || {}), next)[keys.at(-1)] = v;
    setD(next);
  };
  return <Field label={label}>{area ? <Area value={get || ""} onChange={(e) => set(e.target.value)} testId={testId} /> : <Input value={get || ""} onChange={(e) => set(e.target.value)} testId={testId} />}</Field>;
};

export function HomepageEditor() {
  const [d, setD] = useDoc("/admin/content/homepage");
  const [saving, run] = useSaver();
  const refresh = useRefresh();
  if (!d) return <Skel rows={8} />;
  const p = { d, setD };
  const save = () => run(() => adminApi.put("/admin/content/homepage", d)).then((r) => r && refresh("/admin/content"));
  return (
    <>
      <PageTitle title="Homepage" subtitle="Edit hero text, stats, USPs, process steps and technologies."><YBtn loading={saving} onClick={save} testId="homepage-save-btn">Save changes</YBtn></PageTitle>
      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Hero">
          <div className="grid gap-3 sm:grid-cols-2">
            <T {...p} path="hero.badge" label="Badge" testId="hero-badge-input" /><T {...p} path="hero.headline" label="Headline" testId="hero-headline-input" />
            <T {...p} path="hero.highlight" label="Highlighted phrase" testId="hero-highlight-input" /><T {...p} path="hero.subheadline" label="Sub-headline" />
            <T {...p} path="hero.primary_cta" label="Primary CTA label" testId="hero-primary-cta-input" /><T {...p} path="hero.secondary_cta" label="Secondary CTA label" />
            <div className="sm:col-span-2"><T {...p} path="hero.paragraph" label="Paragraph" area /></div>
            <div className="sm:col-span-2"><T {...p} path="hero.trust_line" label="Trust line" /></div>
          </div>
        </Card>
        <Card title="Stats counters"><ObjectList value={d.stats} onChange={(v) => setD({ ...d, stats: v })} fields={[{ key: "label", label: "Label" }, { key: "value", label: "Value", type: "number" }, { key: "suffix", label: "Suffix (e.g. +)" }]} newItem={{ label: "", value: 0, suffix: "+" }} testId="stats-editor" /></Card>
        <Card title="Why choose us (USPs)">
          <div className="mb-3 grid gap-3 sm:grid-cols-2"><T {...p} path="why_us.title" label="Title" /><T {...p} path="why_us.highlight" label="Highlight" /><div className="sm:col-span-2"><T {...p} path="why_us.subtitle" label="Subtitle" area /></div></div>
          <ObjectList value={d.why_us?.items} onChange={(v) => setD({ ...d, why_us: { ...d.why_us, items: v } })} fields={[{ key: "icon", label: "Icon" }, { key: "title", label: "Title" }, { key: "description", label: "Description", type: "textarea" }]} newItem={{ icon: "Sparkles", title: "", description: "" }} testId="usp-editor" />
        </Card>
        <Card title="Process steps"><ObjectList value={d.process} onChange={(v) => setD({ ...d, process: v })} fields={[{ key: "icon", label: "Icon" }, { key: "title", label: "Title" }, { key: "description", label: "Description", type: "textarea" }]} newItem={{ icon: "Rocket", title: "", description: "" }} testId="process-editor" /></Card>
        <Card title="Technologies"><StringList value={d.technologies} onChange={(v) => setD({ ...d, technologies: v })} placeholder="Add technology" testId="tech-editor" /></Card>
        <Card title="Final CTA banner"><div className="space-y-3"><T {...p} path="cta_banner.title" label="Title" /><T {...p} path="cta_banner.subtitle" label="Subtitle" /><T {...p} path="cta_banner.button" label="Button label" /></div></Card>
      </div>
    </>
  );
}

export function ContactPricingEditor() {
  const [c, setC] = useDoc("/admin/content/contact");
  const [pr, setPr] = useDoc("/admin/content/pricing");
  const { data: s } = useAdmin("/admin/settings");
  const [map, setMap] = useState(null);
  const [saving, run] = useSaver();
  const refresh = useRefresh();
  if (!c || !pr || !s) return <Skel rows={8} />;
  const mapUrl = map ?? s.map_embed_url ?? "";
  const save = () => run(async () => {
    await adminApi.put("/admin/content/contact", c);
    await adminApi.put("/admin/content/pricing", pr);
    await adminApi.put("/admin/settings", { map_embed_url: mapUrl });
    refresh();
  });
  return (
    <>
      <PageTitle title="Contact & Pricing Text" subtitle="Contact page content, map and the Pricing page header text."><YBtn loading={saving} onClick={save} testId="contact-save-btn">Save changes</YBtn></PageTitle>
      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Contact page">
          <div className="space-y-3">
            <T d={c} setD={setC} path="headline" label="Heading" testId="contact-headline-input" /><T d={c} setD={setC} path="highlight" label="Highlighted words" />
            <T d={c} setD={setC} path="intro" label="Intro text" area testId="contact-intro-input" />
            <T d={c} setD={setC} path="success_message" label="Form success message" testId="contact-success-input" />
            <Field label="Google Maps embed URL" hint="Google Maps → Share → Embed a map → copy the src URL"><Input value={mapUrl} onChange={(e) => setMap(e.target.value)} testId="contact-map-input" /></Field>
            <p className="label-base mt-2">Extra contact blocks</p>
            <ObjectList value={c.extra_blocks || []} onChange={(v) => setC({ ...c, extra_blocks: v })} fields={[{ key: "title", label: "Title (e.g. Branch office)" }, { key: "text", label: "Text", type: "textarea" }]} newItem={{ title: "", text: "" }} testId="contact-extra-editor" />
          </div>
        </Card>
        <Card title="Pricing page text">
          <div className="space-y-3">
            <T d={pr} setD={setPr} path="headline" label="Heading" testId="pricing-headline-input" /><T d={pr} setD={setPr} path="highlight" label="Highlighted words" />
            <T d={pr} setD={setPr} path="subtitle" label="Subtitle" area /><T d={pr} setD={setPr} path="note" label="Bottom note" area />
          </div>
        </Card>
      </div>
    </>
  );
}

export function MenusEditor() {
  const { data: s } = useAdmin("/admin/settings");
  const [m, setM] = useState(null);
  const [saving, run] = useSaver();
  const refresh = useRefresh();
  useEffect(() => { if (s && !m) setM({ nav_menu: s.nav_menu || [], footer_menu: s.footer_menu || [] }); }, [s, m]);
  if (!m) return <Skel />;
  const DEF = [["Home", "/"], ["Products", "/products"], ["Services", "/services"], ["Pricing", "/pricing"], ["About", "/about"], ["Contact", "/contact"]].map(([label, to]) => ({ label, to }));
  const editor = (k, title) => (
    <Card title={title} action={!m[k].length && <button className="text-xs font-bold text-ocean" onClick={() => setM({ ...m, [k]: DEF })} data-testid={`${k}-load-default`}>Load defaults</button>}>
      <p className="mb-3 text-xs text-muted2">{m[k].length ? "Drag to reorder." : "Empty = default menu is used."} Pages with "Show in navbar/footer" in Page Manager are added automatically.</p>
      <ObjectList value={m[k]} onChange={(v) => setM({ ...m, [k]: v })} fields={[{ key: "label", label: "Label" }, { key: "to", label: "Link (e.g. /pricing)" }]} newItem={{ label: "", to: "/" }} addLabel="Add link" testId={`${k}-editor`} />
    </Card>
  );
  return (
    <>
      <PageTitle title="Navigation Menus"><YBtn loading={saving} onClick={() => run(() => adminApi.put("/admin/settings", m)).then((r) => r && refresh())} testId="menus-save-btn">Save menus</YBtn></PageTitle>
      <div className="grid gap-6 xl:grid-cols-2">{editor("nav_menu", "Navbar links")}{editor("footer_menu", "Footer quick links")}</div>
    </>
  );
}

export function MediaLibrary() {
  const { data, isLoading } = useAdmin("/admin/media");
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    setBusy(true);
    try { for (const f of files) await uploadFile(f); toast.success(`${files.length} file(s) uploaded`); refresh("/admin/media"); } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const copy = (u) => { navigator.clipboard.writeText(`${BACKEND_URL}${u}`); toast.success("URL copied"); };
  return (
    <>
      <PageTitle title="Media Library" subtitle="Images and PDFs up to 10 MB.">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-cta px-5 py-2.5 text-sm font-bold text-navy hover:bg-cta-hover" data-testid="media-upload-btn">
          <Upload className="h-4 w-4" />{busy ? "Uploading..." : "Upload files"}<input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="media-upload-input" />
        </label>
      </PageTitle>
      {isLoading ? <Skel /> : (data || []).length ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="media-grid">
          {data.map((m) => (
            <div key={m.id} className="overflow-hidden rounded-2xl border border-line bg-white shadow-soft" data-testid={`media-item-${m.id}`}>
              <div className="grid aspect-square place-items-center bg-page">{m.content_type?.startsWith("image/") ? <img src={mediaUrl(m.url)} alt={m.original_filename} loading="lazy" className="h-full w-full object-cover" /> : <span className="text-xs font-bold text-muted2">PDF</span>}</div>
              <div className="flex items-center gap-1 p-2">
                <p className="min-w-0 flex-1 truncate text-xs text-body" title={m.original_filename}>{m.original_filename}</p>
                <button onClick={() => copy(m.url)} className="grid h-7 w-7 place-items-center rounded-lg text-muted2 hover:text-ocean" aria-label="Copy URL" data-testid={`media-copy-${m.id}`}><Copy className="h-3.5 w-3.5" /></button>
                <DeleteBtn onConfirm={async () => { await adminApi.delete(`/admin/media/${m.id}`); toast.success("Deleted"); refresh("/admin/media"); }} testId={`media-delete-${m.id}`} />
              </div>
            </div>
          ))}
        </div>
      ) : <Card><p className="py-10 text-center text-sm text-muted2">No files uploaded yet.</p></Card>}
    </>
  );
}
